"use client";

import React from "react";
import { ArrowDownIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  getCategoryGridActionId,
  getChoicePresets,
  getCommitPresets,
  getPresetPresentationType,
  isGenerationPresetAction,
  isVibeBundlePreset,
  normalizePresetLabel,
  orderPresets,
} from "@/lib/agent/presetPresentation";
import {
  getRendererPresentedMessagePresets,
  getRendererPresentedWidgetPresets,
  isFrontReviewDecisionPresetSurface,
} from "@/lib/agent/visiblePresetCapabilities";
import {
  DESIGNER_RENDER_ACTION_PRESENTATION,
  isDesktopFooterOwnedPreset,
  isDesignerRenderActionId,
  type DesignerRenderActionId,
} from "@/lib/agent/designerActionPresentation";
import { normalizeWidgetType } from "@/lib/agent/widgetPlan";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import {
  choiceFooterLabels,
  projectChoiceWidgetSurface,
} from "@/lib/agent/choiceWidgetSurface";
import {
  AgentActionPayload,
  AgentWidget,
  PresetOption,
} from "@/types/createAgent";
import { isWorkspaceSecondaryAction } from "@/lib/designer/workspaceActions";
import { designerPresetsEqual } from "@/lib/designer/actionProjection";
import { type DesktopRenderActionState } from "../desktopRenderAction";
import { useDesignerWorkspace } from "../DesignerWorkspaceProvider";
import { getWidgetConceptLayout } from "./presetMeta";
import { CommitActionStack } from "./CommitActionStack";
import { PresetWidget } from "./PresetWidget";
import { PresetGridTile } from "./PresetGridTile";
import { PresetSelectionFooter } from "./PresetSelectionFooter";
import { VibePresetPicker } from "./VibePresetPicker";
import { scrollIntoNearestViewport } from "./scrollIntoNearestViewport";
import "./AgentPresets.css";
import {
  FrontReviewBackLauncher,
  FrontReviewAmazonLink,
  FrontReviewModifyLauncher,
  FrontReviewPanelBackButton,
  type FrontReviewPanel,
} from "./FrontReviewDecision";

const GENERATE_BACK_LABEL =
  DESIGNER_RENDER_ACTION_PRESENTATION.generate_dress_back.label;
const STANDING_REVIEW_WIDGET_ID = "v2-review-action-panel-standing";
interface AgentPresetsProps {
  messageId?: string;
  presets: PresetOption[];
  widgets?: AgentWidget[];
  onPresetClick: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetSelect?: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetGenerate?: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetChatMore?: (
    preset: PresetOption | null,
    source?: AgentActionPayload["source"]
  ) => void;
  onComposerModifyRequest?: () => void;
  onComposerModifyExit?: () => void;
  disabled?: boolean;
  processingPresetId?: string | null;
  selectedPresetLabel?: string | null;
  freeFrontPreview?: boolean;
  /** Exact render action duplicated by the stable desktop footer. */
  desktopFooterActionId?: DesignerRenderActionId | null;
  /** The footer is resolving this surface and temporarily owns render commits. */
  desktopFooterAuthorityPending?: boolean;
  /** Publishes the exact active render control to the stable desktop footer. */
  onDesktopRenderActionStateChange?: (state: DesktopRenderActionState) => void;
  /** The attached mobile dock controls its own scroll position. */
  compactDock?: boolean;
}

export default function AgentPresets({
  messageId,
  presets,
  widgets,
  onPresetClick,
  onPresetSelect,
  onPresetGenerate,
  onPresetChatMore,
  onComposerModifyRequest,
  onComposerModifyExit,
  disabled = false,
  processingPresetId = null,
  selectedPresetLabel = null,
  desktopFooterActionId,
  desktopFooterAuthorityPending = false,
  compactDock = false,
}: AgentPresetsProps) {
  const orderedPresets = orderPresets(presets);
  // The transcript retains informational context; the small attached dock
  // spends its limited height on widgets with an actionable visible surface.
  const displayWidgets = compactDock
    ? (widgets ?? []).filter(
        widget => getRendererPresentedWidgetPresets(widget).length > 0
      )
    : (widgets ?? []);
  const workspace = useDesignerWorkspace();
  const sharedWorkspace = workspace?.sharedWorkspace === true;
  // Review defaults are appended after the action plan and belong to the
  // workspace. An action inside any other widget was staged by the agent and
  // should remain reachable in the conversation (including the mobile dock).
  const belongsInConversation = (preset: PresetOption, widget?: AgentWidget) =>
    !sharedWorkspace ||
    !isWorkspaceSecondaryAction(preset.actionId) ||
    Boolean(widget && widget.id !== STANDING_REVIEW_WIDGET_ID);
  const [localFrontReviewPanel, setLocalFrontReviewPanel] =
    React.useState<FrontReviewPanel>("main");
  const frontReviewPanel =
    sharedWorkspace && messageId
      ? (workspace.frontReviewPanels[messageId] ?? "main")
      : localFrontReviewPanel;
  const setFrontReviewPanel = (panel: FrontReviewPanel) => {
    if (sharedWorkspace && messageId)
      workspace.setFrontReviewPanel(messageId, panel);
    else setLocalFrontReviewPanel(panel);
  };
  const selectedChoice = messageId
    ? workspace?.interaction.selectedChoicesByMessageId?.[messageId]
    : undefined;
  const isSelectedPreset = (preset: PresetOption, widget?: AgentWidget) =>
    selectedChoice
      ? designerPresetsEqual(selectedChoice.preset, preset) &&
        (!widget ||
          (selectedChoice.source.widgetId === widget.id &&
            selectedChoice.source.widgetVersion === widget.version))
      : Boolean(selectedPresetLabel) &&
        normalizePresetLabel(preset.label) ===
          normalizePresetLabel(selectedPresetLabel ?? "");
  const [showAllFrontReviewBackChoices, setShowAllFrontReviewBackChoices] =
    React.useState(false);
  const [isChangingFrontReviewBackChoice, setIsChangingFrontReviewBackChoice] =
    React.useState(false);
  const activeReviewPanelRef = React.useRef<HTMLDivElement | null>(null);
  // Front-review plans commonly arrive as two widgets (a back catalog plus an
  // action dock), while restored legacy turns arrive as one flat preset list.
  // Flatten only for this presentation decision; clicks still resolve their
  // original widget source in ChatMessageList, preserving server provenance.
  const frontReviewPresets =
    displayWidgets.length > 0
      ? orderPresets(
          displayWidgets
            .filter(widget => widget.active)
            .flatMap(getWidgetPresets)
        )
      : orderedPresets;
  const isFrontReviewDecisionUi =
    isFrontReviewDecisionPresetSurface(frontReviewPresets);

  React.useEffect(() => {
    if (compactDock) return;
    // The initial three-way decision can be appended below the current scroll
    // position just as easily as its nested panels. Keep it above the composer
    // on first render instead of waiting for the user to open Back/Modify.
    if (frontReviewPanel === "main" && !isFrontReviewDecisionUi) return;
    const frame = window.requestAnimationFrame(() => {
      const panel = activeReviewPanelRef.current;
      if (panel) {
        const reduceMotion = window.matchMedia?.(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        scrollIntoNearestViewport(panel, reduceMotion ? "auto" : "smooth");
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    compactDock,
    frontReviewPanel,
    isFrontReviewDecisionUi,
    showAllFrontReviewBackChoices,
    isChangingFrontReviewBackChoice,
    selectedPresetLabel,
  ]);

  // Reset panels only when the CONTENT of the turn changes — array identities
  // are recreated on every parent render (each composer keystroke), which
  // would snap an open modify/back panel shut mid-interaction.
  const turnSignature = [
    ...(presets ?? []).map(preset => `${preset.actionId}:${preset.label}`),
    ...(widgets ?? []).map(widget => `${widget.id}:${widget.version}`),
  ].join("|");
  React.useEffect(() => {
    setLocalFrontReviewPanel("main");
    setShowAllFrontReviewBackChoices(false);
    setIsChangingFrontReviewBackChoice(false);
  }, [turnSignature]);

  React.useEffect(() => {
    setIsChangingFrontReviewBackChoice(false);
  }, [selectedPresetLabel]);

  const choicePresets = getChoicePresets(orderedPresets);
  const selectedChoicePreset =
    choicePresets.find(preset => isSelectedPreset(preset)) ?? null;
  // Presets-only messages are the legacy/restoration path and carry no widget
  // type telling us whether the server intended a catalog. Mirror the modern
  // widget inference: only a full list is a catalog; 2–3 authored options are
  // a focused one-tap decision even when they share a catalog-capable field.
  const sharedCategoryGridActionId = getCategoryGridActionId(choicePresets);
  const categoryGridActionId =
    sharedCategoryGridActionId && choicePresets.length > 5
      ? sharedCategoryGridActionId
      : null;
  const isBackChoiceGrid =
    categoryGridActionId === "set_back_prompt" ||
    categoryGridActionId === "set_back_style";
  const messageFooterLabels = choiceFooterLabels({ isBackChoiceGrid });
  const isVibePresetList =
    choicePresets.length > 0 && choicePresets.every(isVibeBundlePreset);
  // Upload rows open the file picker directly — a selection footer would
  // never enable, so upload-only lists render without one (both paths).
  const isUploadChoiceList =
    choicePresets.length > 0 &&
    choicePresets.every(
      preset => getPresetPresentationType(preset) === "upload_choice"
    );
  const rendererVisibleMessagePresets = getRendererPresentedMessagePresets(
    orderedPresets,
    selectedPresetLabel
  );
  const visibleChoicePresets = getChoicePresets(rendererVisibleMessagePresets);
  // Older persisted responses can contain a small choice set and standing
  // commit actions together. The shared renderer/capability projection keeps
  // those hidden commits out of both this surface and server authorization.
  const visibleCommitPresets = getCommitPresets(rendererVisibleMessagePresets)
    .filter(preset => belongsInConversation(preset))
    .filter(
      preset =>
        !categoryGridActionId || !isGenerationPresetAction(preset.actionId)
    );
  const frontReviewCommitPresets =
    displayWidgets.length > 0
      ? orderPresets(
          displayWidgets
            .filter(widget => widget.active)
            .flatMap(widget =>
              getCommitPresets(getWidgetPresets(widget)).filter(preset =>
                belongsInConversation(preset, widget)
              )
            )
        )
      : getCommitPresets(frontReviewPresets).filter(preset =>
          belongsInConversation(preset)
        );
  const frontReviewChoicePresets = getChoicePresets(frontReviewPresets);
  const frontModifyPresets = frontReviewCommitPresets.filter(
    preset => preset.actionId === "modify_dress_front"
  );
  const backChoicePresets = frontReviewChoicePresets.filter(
    preset =>
      preset.actionId === "set_back_prompt" ||
      preset.actionId === "set_back_style"
  );
  const frontReviewSelectedChoicePreset =
    backChoicePresets.find(preset => isSelectedPreset(preset)) ?? null;
  const getPresetState = (
    preset: PresetOption,
    disabledOverride = disabled
  ) => {
    const presetId = `${preset.actionId}-${preset.label}`;
    const isProcessing = processingPresetId === presetId;
    const isDisabled = disabledOverride || isProcessing;
    const isSelected = isSelectedPreset(preset);

    return { isProcessing, isDisabled, isSelected };
  };
  const isOwnedByDesktopFooter = (preset: PresetOption, widget?: AgentWidget) =>
    (isWorkspaceSecondaryAction(preset.actionId) &&
      !belongsInConversation(preset, widget)) ||
    (!sharedWorkspace && isWorkspaceSecondaryAction(preset.actionId)) ||
    ((desktopFooterAuthorityPending ||
      preset.actionId === desktopFooterActionId) &&
      isDesktopFooterOwnedPreset(preset));
  const desktopFooterPresetClassName = (
    preset: PresetOption,
    widget?: AgentWidget
  ) =>
    sharedWorkspace && isDesignerRenderActionId(preset.actionId)
      ? "hidden"
      : isOwnedByDesktopFooter(preset, widget)
        ? sharedWorkspace
          ? "hidden"
          : "lg:hidden"
        : undefined;

  if (presets.length === 0 && displayWidgets.length === 0) {
    return null;
  }

  const renderPreset = (preset: PresetOption, index: number) => {
    const { isProcessing, isDisabled, isSelected } = getPresetState(preset);

    return (
      <PresetWidget
        key={`${preset.actionId}-${preset.label}-${index}`}
        preset={preset}
        isProcessing={isProcessing}
        isDisabled={isDisabled}
        isSelected={isSelected}
        onClick={() => onPresetClick(preset)}
        className={desktopFooterPresetClassName(preset)}
      />
    );
  };

  const renderGridPreset = (preset: PresetOption, index: number) => {
    const { isProcessing, isDisabled, isSelected } = getPresetState(preset);

    return (
      <PresetGridTile
        key={`${preset.actionId}-${preset.label}-${index}`}
        preset={preset}
        isProcessing={isProcessing}
        isDisabled={isDisabled}
        isSelected={isSelected}
        onClick={() => onPresetSelect?.(preset)}
      />
    );
  };

  const renderChoicePreset = (preset: PresetOption, index: number) => {
    const { isProcessing, isDisabled, isSelected } = getPresetState(preset);

    return (
      <PresetWidget
        key={`${preset.actionId}-${preset.label}-${index}`}
        preset={preset}
        isProcessing={isProcessing}
        isDisabled={isDisabled}
        isSelected={isSelected}
        isSelector
        onClick={() => onPresetSelect?.(preset)}
      />
    );
  };

  const renderFrontReviewBackChoice = (preset: PresetOption) => {
    const { isProcessing, isDisabled, isSelected } = getPresetState(preset);

    return (
      <PresetWidget
        key={`${preset.actionId}-${normalizePresetLabel(preset.label)}`}
        preset={preset}
        isProcessing={isProcessing}
        isDisabled={isDisabled}
        isSelected={isSelected}
        isSelector
        compact
        onClick={() => {
          setIsChangingFrontReviewBackChoice(false);
          onPresetSelect?.(preset);
        }}
      />
    );
  };

  const renderAdvancePreset = (preset: PresetOption, index: number) => {
    const { isProcessing, isDisabled } = getPresetState(preset);

    return (
      <PresetWidget
        key={`${preset.actionId}-${preset.label}-${index}`}
        preset={preset}
        isProcessing={isProcessing}
        isDisabled={isDisabled}
        isSelected={false}
        // A one-tap field choice means "use this detail and advance". It is
        // not the explicit "Continue without generating" footer action, so do
        // not tag it as exploreMore and strand a render-ready design in chat.
        onClick={() => onPresetClick(preset)}
      />
    );
  };

  // Flat section — the packaging card around the widget group is the only box
  // (CreateChatActionStyle.md principle 1); no nested card chrome.
  const renderFrontReviewBackGrid = () => {
    const suggestedChoiceCount = 3;
    const isShowingSelectedChoice =
      !!frontReviewSelectedChoicePreset && !isChangingFrontReviewBackChoice;
    const visibleBackChoices = isShowingSelectedChoice
      ? [frontReviewSelectedChoicePreset]
      : showAllFrontReviewBackChoices
        ? backChoicePresets
        : backChoicePresets.slice(0, suggestedChoiceCount);
    const hiddenChoiceCount = Math.max(
      0,
      backChoicePresets.length - suggestedChoiceCount
    );

    return (
      <div data-front-review-back-section className="grid min-w-0 gap-2">
        <div
          data-front-review-back-options
          data-expanded={showAllFrontReviewBackChoices || undefined}
          className={cn(
            "grid min-w-0 gap-2",
            showAllFrontReviewBackChoices &&
              "max-h-[38svh] overflow-y-auto overscroll-contain pr-1"
          )}
        >
          {visibleBackChoices.map(renderFrontReviewBackChoice)}
        </div>
        {isShowingSelectedChoice ? (
          <button
            type="button"
            data-front-review-change-back-style
            disabled={disabled}
            onClick={() => {
              const selectedIndex = backChoicePresets.findIndex(
                preset =>
                  normalizePresetLabel(preset.label) ===
                  normalizePresetLabel(
                    frontReviewSelectedChoicePreset?.label ?? ""
                  )
              );
              setShowAllFrontReviewBackChoices(
                selectedIndex >= suggestedChoiceCount
              );
              setIsChangingFrontReviewBackChoice(true);
              window.requestAnimationFrame(() => {
                const options =
                  activeReviewPanelRef.current?.querySelectorAll<HTMLElement>(
                    "[data-front-review-back-options] button"
                  );
                const selectedOption = Array.from(options ?? []).find(
                  option => option.getAttribute("aria-pressed") === "true"
                );
                (selectedOption ?? options?.[0])?.focus({
                  preventScroll: true,
                });
              });
            }}
            className={cn(
              "mx-auto min-h-8 rounded-lg px-3 py-1 text-[13px] font-semibold text-brand-logo transition-colors",
              "hover:bg-brand-logo/10 hover:text-brand-logo-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              disabled && "cursor-default opacity-50"
            )}
          >
            Change back style
          </button>
        ) : (
          <div className="flex min-w-0 flex-wrap items-center justify-center gap-x-1 gap-y-0.5">
            {hiddenChoiceCount > 0 && (
              <button
                type="button"
                data-front-review-more-back-styles
                disabled={disabled}
                onClick={() =>
                  setShowAllFrontReviewBackChoices(showAll => !showAll)
                }
                className={cn(
                  "min-h-8 rounded-lg px-2 py-1 text-[12px] font-semibold text-brand-logo transition-colors",
                  "hover:bg-brand-logo/10 hover:text-brand-logo-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  disabled && "cursor-default opacity-50"
                )}
              >
                {showAllFrontReviewBackChoices
                  ? "Show suggested styles"
                  : `See ${hiddenChoiceCount} more styles`}
              </button>
            )}
            <span className="text-[11px] font-medium leading-snug text-muted-foreground">
              {hiddenChoiceCount > 0
                ? "or describe yours below"
                : "Describe your own below"}
            </span>
          </div>
        )}
        {isShowingSelectedChoice ? (
          <PresetSelectionFooter
            selectedPreset={frontReviewSelectedChoicePreset}
            disabled={disabled}
            onGenerate={onPresetGenerate}
            onChatMore={onPresetChatMore}
            generateLabel={GENERATE_BACK_LABEL}
            chatMoreLabel="Explore back ideas"
            compact
            hideGenerateUntilSelection
          />
        ) : null}
      </div>
    );
  };

  const renderWidgetPreset = (
    preset: PresetOption,
    index: number,
    widgetDisabled: boolean,
    mode: "commit" | "choice" | "grid" | "advance" = "commit",
    widget?: AgentWidget
  ) => {
    const { isProcessing, isDisabled } = getPresetState(preset, widgetDisabled);
    const isSelected = isSelectedPreset(preset, widget);

    if (mode === "grid") {
      return (
        <PresetGridTile
          key={`${preset.actionId}-${preset.label}-${index}`}
          preset={preset}
          isProcessing={isProcessing}
          isDisabled={isDisabled}
          isSelected={isSelected}
          onClick={() =>
            onPresetSelect?.(
              preset,
              widget
                ? { widgetId: widget.id, widgetVersion: widget.version }
                : undefined
            )
          }
        />
      );
    }

    return (
      <PresetWidget
        key={`${preset.actionId}-${preset.label}-${index}`}
        preset={preset}
        isProcessing={isProcessing}
        isDisabled={isDisabled}
        isSelected={mode === "advance" ? false : isSelected}
        isSelector={mode === "choice"}
        onClick={() =>
          mode === "choice"
            ? onPresetSelect?.(
                preset,
                widget
                  ? { widgetId: widget.id, widgetVersion: widget.version }
                  : undefined
              )
            : onPresetClick(
                preset,
                widget
                  ? { widgetId: widget.id, widgetVersion: widget.version }
                  : undefined
              )
        }
      />
    );
  };

  const renderWidgetHeader = (widget: AgentWidget) => {
    if (!widget.title && !widget.summary && !widget.staleReason) {
      return null;
    }

    const summary = widget.summary;
    const widgetType = normalizeWidgetType(widget.type);
    const hasCompactMobileHeader =
      widgetType === "concept_picker" || widgetType === "upload_request";

    return (
      <div className="grid min-w-0 gap-1 px-1">
        {widget.title && !compactDock && (
          <span className="block min-w-0 text-sm font-semibold leading-snug text-foreground [overflow-wrap:anywhere]">
            {widget.title}
          </span>
        )}
        {(summary || widget.staleReason) && (
          <span
            className={cn(
              "block min-w-0 text-xs font-medium leading-snug text-muted-foreground [overflow-wrap:anywhere]",
              hasCompactMobileHeader && "hidden sm:line-clamp-2"
            )}
          >
            {widget.staleReason || summary}
          </span>
        )}
      </div>
    );
  };

  // Flat key/value rows — the packaging card around the widget group is the
  // only box; items must not read as nested cards.
  const renderWidgetItems = (widget: AgentWidget) => {
    if (!widget.items || widget.items.length === 0) {
      return null;
    }

    return (
      <div className="grid min-w-0 gap-1.5">
        {widget.items.map(item => (
          <div
            key={`${item.label}-${item.value}`}
            className="flex min-w-0 items-baseline gap-2"
          >
            <span className="w-20 shrink-0 text-[11px] font-semibold uppercase leading-snug text-muted-foreground">
              {item.label}
            </span>
            <span
              className={cn(
                "min-w-0 text-sm font-medium leading-snug [overflow-wrap:anywhere]",
                item.tone === "positive" && "text-success",
                item.tone === "warning" && "text-warning",
                (!item.tone || item.tone === "neutral") && "text-foreground"
              )}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderBriefSummaryWidget = (widget: AgentWidget, index: number) => (
    <details
      key={`${widget.id}-${index}`}
      data-agent-widget={widget.type}
      data-agent-widget-concept={normalizeWidgetType(widget.type)}
      data-agent-widget-layout={getWidgetConceptLayout(widget)}
      className={cn(
        "group min-w-0 rounded-xl border border-border/70 bg-muted/20 px-3 py-2",
        !widget.active && "opacity-60"
      )}
    >
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <span>{widget.title || "Saved details"}</span>
        <span className="shrink-0 text-xs font-medium text-muted-foreground group-open:hidden">
          View
        </span>
        <span className="hidden shrink-0 text-xs font-medium text-muted-foreground group-open:inline">
          Hide
        </span>
      </summary>
      <div className="mt-2 grid min-w-0 gap-2 border-t border-border/60 pt-2">
        {(widget.summary || widget.staleReason) && (
          <span className="block min-w-0 text-xs font-medium leading-snug text-muted-foreground [overflow-wrap:anywhere]">
            {widget.staleReason || widget.summary}
          </span>
        )}
        {renderWidgetItems(widget)}
      </div>
    </details>
  );

  const renderActionWidget = (widget: AgentWidget, index: number) => {
    const widgetPresets = orderPresets(getWidgetPresets(widget));
    const widgetDisabled = disabled || !widget.active;
    // Commit actions get the tiered stack (hero/support/exit); any choice
    // presets an action widget carries keep their selector rows.
    const widgetCommitPresets = getCommitPresets(widgetPresets).filter(preset =>
      belongsInConversation(preset, widget)
    );
    const widgetChoicePresets = getChoicePresets(widgetPresets);
    if (
      sharedWorkspace &&
      !widgetCommitPresets.length &&
      !widgetChoicePresets.length
    )
      return null;

    return (
      <div
        key={`${widget.id}-${index}`}
        data-agent-widget={widget.type}
        data-agent-widget-concept={normalizeWidgetType(widget.type)}
        data-agent-widget-layout={getWidgetConceptLayout(widget)}
        aria-disabled={widgetDisabled}
        className={cn(
          "grid min-w-0 gap-2",
          widgetCommitPresets.length > 0 &&
            widgetChoicePresets.length === 0 &&
            widgetCommitPresets.every(preset =>
              isOwnedByDesktopFooter(preset, widget)
            ) &&
            (sharedWorkspace ? "hidden" : "lg:hidden"),
          !widget.active && "pointer-events-none opacity-60"
        )}
      >
        {renderWidgetHeader(widget)}
        {renderWidgetItems(widget)}
        {widgetCommitPresets.length > 0 && (
          <CommitActionStack
            presets={widgetCommitPresets}
            getPresetState={preset => getPresetState(preset, widgetDisabled)}
            onPresetClick={preset =>
              onPresetClick(preset, {
                widgetId: widget.id,
                widgetVersion: widget.version,
              })
            }
            presetClassName={preset =>
              desktopFooterPresetClassName(preset, widget)
            }
          />
        )}
        {widgetChoicePresets.length > 0 && (
          <div className="grid min-w-0 gap-2">
            {/* Choice presets inside an action dock have no selection footer,
                so they stay immediate acts — select-only rows would strand
                the turn (see PR #227). */}
            {widgetChoicePresets.map((preset, presetIndex) =>
              renderWidgetPreset(
                preset,
                presetIndex,
                widgetDisabled,
                "commit",
                widget
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const renderChoiceWidget = (widget: AgentWidget, index: number) => {
    const surface = projectChoiceWidgetSurface(widget);
    const widgetDisabled = disabled || !widget.active;
    // includeDisabled preserves stale widgets visually while their disabled
    // state still prevents clicks. Server authorization calls the same helper
    // without that display-only option and therefore grants them nothing.
    const rendererVisibleWidgetPresets = getRendererPresentedWidgetPresets(
      widget,
      { includeDisabled: true }
    );
    const rendererVisibleWidgetCommitPresets = getCommitPresets(
      rendererVisibleWidgetPresets
    );
    const widgetChoicePresets = surface.choicePresets;
    const rendererVisibleWidgetChoices = getChoicePresets(
      rendererVisibleWidgetPresets
    );
    const widgetSelectedChoicePreset =
      widgetChoicePresets.find(preset => isSelectedPreset(preset, widget)) ??
      null;
    const {
      categoryGridActionId: widgetCategoryGridActionId,
      isVibePresetList: isWidgetVibePresetList,
      isUploadChoiceList: isWidgetUploadChoiceList,
      isCuratedOneTapChoice,
      rendersSelectionFooter: rendersWidgetSelectionFooter,
    } = surface;
    const widgetCommitPresets = rendererVisibleWidgetCommitPresets
      .filter(preset => belongsInConversation(preset, widget))
      .filter(
        preset =>
          !rendersWidgetSelectionFooter ||
          !isGenerationPresetAction(preset.actionId)
      );
    const visibleWidgetChoicePresets = rendererVisibleWidgetChoices;
    return (
      <div
        key={`${widget.id}-${index}`}
        data-agent-widget={widget.type}
        data-agent-widget-concept={normalizeWidgetType(widget.type)}
        data-agent-widget-layout={getWidgetConceptLayout(widget)}
        aria-disabled={widgetDisabled}
        className={cn(
          "grid min-w-0 gap-2",
          !widget.active && "pointer-events-none opacity-60"
        )}
      >
        {renderWidgetHeader(widget)}
        {widgetCommitPresets.length > 0 && (
          <CommitActionStack
            presets={widgetCommitPresets}
            getPresetState={preset => getPresetState(preset, widgetDisabled)}
            onPresetClick={preset =>
              onPresetClick(preset, {
                widgetId: widget.id,
                widgetVersion: widget.version,
              })
            }
            presetClassName={preset =>
              desktopFooterPresetClassName(preset, widget)
            }
          />
        )}
        {widgetChoicePresets.length > 0 && isWidgetVibePresetList && (
          <VibePresetPicker
            presets={widgetChoicePresets}
            disabled={widgetDisabled}
            processingPresetId={processingPresetId}
            onPresetChatMore={preset =>
              onPresetChatMore?.(preset, {
                widgetId: widget.id,
                widgetVersion: widget.version,
              })
            }
          />
        )}
        {widgetChoicePresets.length > 0 && !isWidgetVibePresetList && (
          <>
            <div
              data-preset-grid={widgetCategoryGridActionId ?? undefined}
              data-mobile-catalog-scroll={
                widgetCategoryGridActionId ? true : undefined
              }
              className={cn(
                "grid min-w-0 gap-2",
                widgetCategoryGridActionId
                  ? "agent-preset-catalog grid-cols-2"
                  : "agent-preset-choices grid-cols-1"
              )}
            >
              {visibleWidgetChoicePresets.map((preset, presetIndex) =>
                renderWidgetPreset(
                  preset,
                  presetIndex,
                  widgetDisabled,
                  surface.presetMode,
                  widget
                )
              )}
            </div>
            {/* Upload rows open the file picker directly — a selection footer
                would never enable, so it's dead UI on upload widgets. */}
            {!isWidgetUploadChoiceList && !isCuratedOneTapChoice && (
              <PresetSelectionFooter
                selectedPreset={widgetSelectedChoicePreset}
                disabled={widgetDisabled}
                onGenerate={preset =>
                  onPresetGenerate?.(preset, {
                    widgetId: widget.id,
                    widgetVersion: widget.version,
                  })
                }
                onChatMore={preset =>
                  onPresetChatMore?.(preset, {
                    widgetId: widget.id,
                    widgetVersion: widget.version,
                  })
                }
                generateLabel={surface.footerLabels.generateLabel}
                chatMoreLabel={surface.footerLabels.chatMoreLabel}
                costLabel="1 credit"
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderAgentWidget = (widget: AgentWidget, index: number) => {
    switch (normalizeWidgetType(widget.type)) {
      case "brief_summary":
        return renderBriefSummaryWidget(widget, index);
      case "concept_picker":
      case "field_picker":
      case "catalog_picker":
      case "upload_request":
        return renderChoiceWidget(widget, index);
      case "action_dock":
      case "review_action_panel":
        return renderActionWidget(widget, index);
      default:
        return renderActionWidget(widget, index);
    }
  };

  if (isFrontReviewDecisionUi) {
    const primaryCommitPresets = frontReviewCommitPresets.filter(
      preset =>
        preset.actionId === "submit_quote" ||
        preset.actionId === "start_virtual_try_on"
    );
    const commitWidget = (preset: PresetOption) => {
      const matches = displayWidgets.filter(
        widget =>
          widget.active &&
          getWidgetPresets(widget).some(candidate =>
            designerPresetsEqual(candidate, preset)
          )
      );
      return matches.length === 1 ? matches[0] : undefined;
    };
    const openFrontReviewModify = () => {
      setFrontReviewPanel("modify");
      if (frontModifyPresets.length === 0) {
        onComposerModifyRequest?.();
      }
    };
    const openFrontReviewBack = () => {
      onComposerModifyExit?.();
      setShowAllFrontReviewBackChoices(false);
      setIsChangingFrontReviewBackChoice(false);
      setFrontReviewPanel("back");
    };

    if (frontReviewPanel === "modify") {
      return (
        <div
          data-front-review-modify-panel
          ref={activeReviewPanelRef}
          className="grid w-full min-w-0 scroll-mb-3 gap-2"
        >
          <FrontReviewPanelBackButton
            disabled={disabled}
            panel="modify"
            autoFocus={frontModifyPresets.length > 0}
            onClick={() => {
              setFrontReviewPanel("main");
              onComposerModifyExit?.();
              window.requestAnimationFrame(() =>
                activeReviewPanelRef.current
                  ?.querySelector<HTMLElement>(
                    "[data-front-review-modify-front]"
                  )
                  ?.focus({ preventScroll: true })
              );
            }}
          />
          <span className="min-w-0 px-1 text-sm font-semibold leading-snug text-foreground">
            Choose a front change
          </span>
          {frontModifyPresets.length > 0 ? (
            <div className="grid gap-2">
              {frontModifyPresets.map(renderPreset)}
            </div>
          ) : (
            <div
              role="note"
              data-front-review-composer-guidance
              className="flex items-start gap-2 px-1 py-1.5 text-sm leading-5 text-muted-foreground"
            >
              <ArrowDownIcon
                size={16}
                weight="bold"
                aria-hidden="true"
                className="mt-0.5 shrink-0 text-brand-logo"
              />
              <span>
                Use the message box below to describe one change, then send it.
              </span>
            </div>
          )}
        </div>
      );
    }

    if (frontReviewPanel === "back") {
      return (
        <div
          data-front-review-back-panel
          ref={activeReviewPanelRef}
          className="grid w-full min-w-0 scroll-mb-3 gap-2"
        >
          <FrontReviewPanelBackButton
            disabled={disabled}
            panel="back"
            onClick={() => {
              setFrontReviewPanel("main");
              onComposerModifyExit?.();
              window.requestAnimationFrame(() =>
                activeReviewPanelRef.current
                  ?.querySelector<HTMLElement>(
                    "[data-front-review-design-back]"
                  )
                  ?.focus({ preventScroll: true })
              );
            }}
          />
          <span className="min-w-0 px-1 text-sm font-semibold leading-snug text-foreground">
            Choose a back style
          </span>
          {renderFrontReviewBackGrid()}
        </div>
      );
    }

    return (
      <div
        ref={activeReviewPanelRef}
        data-front-review-decision-ui
        className="grid w-full min-w-0 scroll-mb-3 gap-2"
      >
        <CommitActionStack
          presets={primaryCommitPresets}
          getPresetState={getPresetState}
          onPresetClick={preset => {
            const widget = commitWidget(preset);
            onPresetClick(
              preset,
              widget
                ? { widgetId: widget.id, widgetVersion: widget.version }
                : undefined
            );
          }}
          presetClassName={preset =>
            desktopFooterPresetClassName(preset, commitWidget(preset))
          }
          supportChildren={[
            <FrontReviewAmazonLink key="amazon" />,
            <FrontReviewModifyLauncher
              key="modify-launcher"
              disabled={disabled}
              onClick={openFrontReviewModify}
            />,
            <FrontReviewBackLauncher
              key="back-launcher"
              disabled={disabled}
              onClick={openFrontReviewBack}
            />,
          ]}
        />
      </div>
    );
  }

  if (displayWidgets.length > 0) {
    return (
      <div
        data-agent-widget-stack
        className="agent-presets grid w-full min-w-0 gap-2"
      >
        {displayWidgets.map(renderAgentWidget)}
      </div>
    );
  }

  return (
    <div className="agent-presets grid w-full min-w-0 gap-2">
      {visibleCommitPresets.length > 0 && (
        <CommitActionStack
          presets={visibleCommitPresets}
          getPresetState={getPresetState}
          onPresetClick={preset => onPresetClick(preset)}
          presetClassName={desktopFooterPresetClassName}
        />
      )}

      {choicePresets.length > 0 && isVibePresetList && (
        <VibePresetPicker
          presets={choicePresets}
          disabled={disabled}
          processingPresetId={processingPresetId}
          onPresetChatMore={onPresetChatMore}
        />
      )}

      {choicePresets.length > 0 && !isVibePresetList && (
        <>
          <div
            data-preset-grid={categoryGridActionId ?? undefined}
            data-mobile-catalog-scroll={categoryGridActionId ? true : undefined}
            className={cn(
              "grid min-w-0 gap-2",
              categoryGridActionId
                ? "agent-preset-catalog grid-cols-2"
                : "agent-preset-choices grid-cols-1"
            )}
          >
            {visibleChoicePresets.map(
              categoryGridActionId
                ? renderGridPreset
                : isUploadChoiceList
                  ? renderChoicePreset
                  : renderAdvancePreset
            )}
          </div>
          {!isUploadChoiceList && categoryGridActionId && (
            <PresetSelectionFooter
              selectedPreset={selectedChoicePreset}
              disabled={disabled}
              onGenerate={onPresetGenerate}
              onChatMore={onPresetChatMore}
              generateLabel={messageFooterLabels.generateLabel}
              chatMoreLabel={messageFooterLabels.chatMoreLabel}
              costLabel="1 credit"
            />
          )}
        </>
      )}
    </div>
  );
}
